import React, { useEffect, useRef, useState } from 'react';
import Sidebar, {
  RAIL_WIDTH, NAV_EXPANDED_WIDTH, NAV_WIDTH_VAR, NAV_TRANSITION_MS, navTransition,
} from './Sidebar';
import UserMenu from './UserMenu';
import ToastHost, { ToastProvider } from './ToastHost';
import FeedbackButton from './FeedbackButton';
import { useAuth } from '../AuthContext';
import { CompanyLogo, companyLogos } from '../assets/companyLogos';
import { LAYOUT_TOP_OFFSET } from '../config';

// McKinsey-inspired color palette - exported for use in other components
export const colors = {
  deepBlue: '#051C2C',
  electricBlue: '#2251FF',
  cyan: '#00A9F4',
  white: '#FFFFFF',
  lightGray: '#F5F7FA',
  mediumGray: '#E1E5EB',
  textMuted: '#8A9BAE',
  darkText: '#1A2B3C',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
};

function AppLayout({ children }) {
  const { user } = useAuth();
  // Lane 1363: the nav rests as the icon rail and opens on hover. When it
  // opens the page MAKES ROOM (Ryan): it slides right and gets narrower, it is
  // never covered. The one source of the current width is the CSS variable
  // NAV_WIDTH_VAR on this root for the fixed elements in page stylesheets
  // that are anchored to the left; the header and the page use the same value
  // directly. All of them move together with the same transition.
  // Right-anchored controls (Feedback, chat bubble, toasts) already sit at the
  // right edge, which does not move.
  const [navExpanded, setNavExpanded] = useState(false);
  const navWidth = `${navExpanded ? NAV_EXPANDED_WIDTH : RAIL_WIDTH}px`;

  // The page area changed width, the same as a window resize for anything on
  // it: once the nav has moved, send one `resize` so pop-ups placed by screen
  // position (filter drop-downs, menus) close or measure again instead of
  // staying where the page used to be.
  const navMoved = useRef(false);
  useEffect(() => {
    if (!navMoved.current) { navMoved.current = true; return undefined; }
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), NAV_TRANSITION_MS);
    return () => clearTimeout(t);
  }, [navExpanded]);

  return (
    <ToastProvider>
    <div
      data-nav-expanded={navExpanded ? 'true' : 'false'}
      style={{
        [NAV_WIDTH_VAR]: navWidth,
        height: `calc(100vh - ${LAYOUT_TOP_OFFSET}px)`,
        overflow: 'hidden',
        backgroundColor: colors.lightGray,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <ToastHost />
      {/* Top Header Bar */}
      <header
        style={{
          position: 'fixed',
          top: LAYOUT_TOP_OFFSET,
          left: navWidth,
          transition: navTransition('left'),
          right: 0,
          height: '60px',
          backgroundColor: colors.deepBlue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          zIndex: 100,
          boxShadow: 'var(--shadow-md)',
        }}
      >
        {/* Left side - Company context */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {user?.company_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {companyLogos.hasOwnProperty(user.company_name) ? (
                <CompanyLogo name={user.company_name} width={90} height={32} />
              ) : (
                <div style={{ color: colors.white, fontSize: 'var(--font-size-base)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ opacity: 0.7 }}>Company:</span>
                  <span style={{ fontWeight: 'var(--font-weight-semibold)', background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '4px' }}>
                    {user.company_name}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side - User menu */}
        <UserMenu />
      </header>

      {/* Sidebar */}
      <Sidebar onExpandedChange={setNavExpanded} />

      {/* Main Content */}
      <div
        style={{
          marginLeft: navWidth,
          transition: navTransition('margin-left'),
          marginTop: '60px',
          height: `calc(100vh - 60px - ${LAYOUT_TOP_OFFSET}px)`,
          overflow: 'hidden',
        }}
      >
        {/* Content Area */}
        <main
          className="content-fade-in"
          style={{
            height: '100%',
            overflow: 'auto',
            backgroundColor: colors.lightGray,
          }}
        >
          {children}
        </main>
      </div>

      {/* Floating Feedback button — company portal only; never mounted on
          admin or supplier portals because those use AdminLayout /
          SupplierLayout wrappers instead of this one. */}
      <FeedbackButton />
    </div>
    </ToastProvider>
  );
}

export default AppLayout;
